// Leave Review Form Data Types (2025)
// ✅ UPDATED: Aligned with actual property_reviews table schema (47 fields)
// ✅ VALIDATED: All enum values match database enums exactly

export interface PersonalDetails {
  firstName: string;   // → first_name (varchar, required)
  lastName: string;    // → last_name (varchar, required)
  email: string;       // → email_address (varchar, required)
  countryCode: string; // Form field (not stored)
  phone: string;       // → phone_number (varchar, required)
}

export interface VerificationStatus {
  isVerified: boolean;
  phoneNumber?: string;
  verificationDate?: string;
}

export interface PropertyLocation {
  country: string;           // → country (varchar, required)
  state: string;             // → state (varchar, required)
  city: string;              // → city (varchar, required)
  streetAddress?: string;    // → street_address (varchar, nullable)
  addressLine2?: string;     // → address_line_2 (varchar, nullable)
  zipCode?: string;          // → zip_code (varchar, nullable)
  latitude?: number;         // → latitude (numeric, nullable)
  longitude?: number;        // → longitude (numeric, nullable)
}

export interface PropertyDetails {
  propertyTypeSlug: string;  // → property_type_slug (varchar, required)
  propertyCategory?: string; // → property_category_slug (varchar, nullable)
  bedrooms?: number;         // → bedroom_size (varchar, nullable)
  propertyId?: string;       // → property_id (varchar, nullable)
  generatedTitle?: string;   // → generated_title (varchar, nullable)
}

export interface TenancyInformation {
  moveInDate: string;                    // → move_in_date (date, required)
  moveOutDate?: string;                  // → move_out_date (date, nullable)
  currentlyLivingThere: boolean;         // → currently_living_there (boolean, default false)
  rentalHistory?: Array<{               // → rental_history (jsonb, nullable)
    rentYear: string;
    rentAmount: string;
    reason: string;
  }>;
}

export interface FinancialInformation {
  rentPrice: number;                     // → rent_price (numeric, required)
  rentPriceCurrency: 'NGN' | 'USD' | 'GBP' | 'EUR'; // → rent_price_currency (enum, default 'NGN')
}

// ✅ NEW: Complete utilities interfaces matching database schema
export interface UtilitiesElectricity {
  electricityType: 'phcn' | 'generator' | 'inverter';           // → electricity_type (enum, required)
  prepaidMeter: 'yes' | 'no' | 'unknown';                       // → prepaid_meter (enum, required)
  electricityBand: 'A' | 'B' | 'C' | 'D' | 'E' | 'unknown';     // → electricity_band (enum, default 'unknown')
  electricityTariffCurrency?: string;                            // → electricity_tariff_currency (varchar, nullable)
  tariffPerKwh: number;                                          // → tarrif_per_kwh (integer, default 0)
  electricityDebtStatus: 'paid' | 'owing' | 'unknown';          // → electricity_debt_status (enum, default 'unknown')
  generatorType?: 'diesel' | 'self_service' | 'none';           // → generator_type (enum, nullable)
}

export interface UtilitiesWater {
  waterStatus: 'treated' | 'fair' | 'bad' | 'unknown';          // → water_status (enum, default 'unknown')
  h20IsDrinkable: 'yes' | 'no' | 'unknown';                     // → h20_is_drinkable (enum, default 'unknown')
}

export interface PropertyAmenities {
  selectedAmenities: string[];
  amenitiesByCategory: Record<string, string[]>;
  // This maps to → property_amenities (jsonb, required)
}

export interface ReviewContent {
  summaryRatings: {                      // → summary_ratings (jsonb, required)
    overall: number;
    condition: number;
    value: number;
    management: number;
    safety: number;
    price: number;
  };
  pros: string[];                        // → pros (jsonb, required)
  cons: string[];                        // → cons (jsonb, required)
  detailedReview: string;                // → detailed_review (text, required)
  rentAgain: boolean;                    // → rent_again (boolean, required)
}

export interface DocumentUploads {
  propertyImages: {                      // → tenant_property_images (jsonb, nullable)
    files: File[];
    previews: string[];
    metadata: Array<{ name: string; type: string; size: number }>;
  };
  identityDocuments: {                   // → tenant_id_doc (ARRAY<JSONB>, nullable)
    files: File[];
    previews: string[];
    metadata: Array<{ name: string; type: string; size: number; filePath?: string }>;
  };
  residencyDocuments: {                  // → tenant_por_doc (ARRAY<JSONB>, nullable)
    files: File[];
    previews: string[];
    metadata: Array<{ name: string; type: string; size: number; filePath?: string }>;
  };
}

export interface CommissionData {
  commissionIntent: boolean;             // → commission_intent (boolean, default false)
  commissionPayout?: {                   // → commission_payout (jsonb, nullable)
    optIn: boolean;
    bankDetails?: {
      bankCode: string;
      bankName: string;
      accountNumber: string;
      accountName: string;
    };
  };
}

export interface SubmissionData {
  reviewStatus: 'pending' | 'published' | 'rejected' | 'in review'; // → review_status (enum, default 'pending')
  termsAccepted: {
    terms: boolean;
    privacy: boolean;
  }; // ✅ 2025 PATTERN: Matches Zod schema contract for proper validation
}

// ✅ NEW: Database-specific fields for complete data handling
export interface DatabaseMetadata {
  reviewId?: string;                     // → review_id (uuid, primary key, auto-generated)
  referenceId?: string;                  // → reference_id (varchar, unique, auto-generated)
  tenantId?: string;                     // → tenant_id (uuid, required, from auth)
  moderatedBy?: string;                  // → moderated_by (uuid, nullable)
  moderatedAt?: string;                  // → moderated_at (timestamp, nullable)
  rejectionReason?: string;              // → rejection_reason (text, nullable)
  createdAt?: string;                    // → created_at (timestamptz, auto-generated)
  updatedAt?: string;                    // → updated_at (timestamptz, nullable)
}

// Complete Leave Review Form Data Structure
export interface LeaveReviewFormData {
  // Step 1 & 2: Personal and Verification
  personalDetails: PersonalDetails;
  verification: VerificationStatus;
  
  // Step 3: Property Details
  propertyLocation: PropertyLocation;
  propertyDetails: PropertyDetails;
  tenancyInformation: TenancyInformation;
  financialInformation: FinancialInformation;
  propertyAmenities: PropertyAmenities;
  
  // Utilities (part of step 3)
  utilitiesElectricity: UtilitiesElectricity;
  utilitiesWater: UtilitiesWater;
  
  // Step 4: Experience/Review
  reviewContent: ReviewContent;
  
  // Step 5: Documents
  documents: DocumentUploads;
  
  // Step 6: Commission & Submission
  commission: CommissionData;
  submission: SubmissionData;
  
  // Database metadata (not part of form, but needed for data handling)
  databaseMetadata?: DatabaseMetadata;
  
  // Form metadata
  lastUpdated: string;
  currentStep: string;
  completedSteps: string[];
}

// Step validation configuration
export interface StepValidation {
  stepName: string;
  required: boolean;
  fields: string[];
  customValidator?: (data: Partial<LeaveReviewFormData>) => boolean;
}

// Form data response type for server actions
export interface LeaveReviewSubmissionResult {
  success: boolean;
  reviewId?: string;
  referenceId?: string;
  error?: string;
  validationErrors?: Record<string, string>;
}

// ✅ UPDATED: Initial form data with correct default values matching database defaults
export const createInitialFormData = (): LeaveReviewFormData => ({
  personalDetails: {
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '+234',
    phone: '',
  },
  verification: {
    isVerified: false,
  },
  propertyLocation: {
    country: 'Nigeria', // ✅ 2025 BEST PRACTICE: Default to most common value
    state: '',
    city: '',
  },
  propertyDetails: {
    propertyTypeSlug: '',
    propertyCategory: '',
    bedrooms: 0,
  },
  tenancyInformation: {
    moveInDate: '',
    currentlyLivingThere: false,
  },
  financialInformation: {
    rentPrice: 0,
    rentPriceCurrency: 'NGN',  // Matches database default
  },
  propertyAmenities: {
    selectedAmenities: [],
    amenitiesByCategory: {},
  },
  utilitiesElectricity: {
    electricityType: 'phcn',       // ✅ 2025 BEST PRACTICE: Default to most common value
    prepaidMeter: 'unknown',       // Matches database default
    electricityBand: 'unknown',    // Matches database default
    tariffPerKwh: 0,               // Matches database default
    electricityDebtStatus: 'unknown', // Matches database default
  },
  utilitiesWater: {
    waterStatus: 'unknown',        // Matches database default
    h20IsDrinkable: 'unknown',     // ✅ 2025 BEST PRACTICE: Default to unknown instead of empty string
  },
  reviewContent: {
    summaryRatings: {
      overall: 0,
      condition: 0,
      value: 0,
      management: 0,
      safety: 0,
      price: 0,
    },
    pros: [],
    cons: [],
    detailedReview: '',
    rentAgain: false,
  },
  documents: {
    propertyImages: {
      files: [],
      previews: [],
      metadata: [],
    },
    identityDocuments: {
      files: [],
      previews: [],
      metadata: [],
    },
    residencyDocuments: {
      files: [],
      previews: [],
      metadata: [],
    },
  },
  commission: {
    commissionIntent: false,      // Matches database default
  },
  submission: {
    reviewStatus: 'pending',      // Matches database default
    termsAccepted: {
      terms: false,
      privacy: false,
    },        // Matches database default
  },
  lastUpdated: new Date().toISOString(),
  currentStep: 'preparation',
  completedSteps: [],
});

// ✅ NEW: Database field mapping for server actions
export interface DatabaseFieldMapping {
  // Maps form field → database column
  'personalDetails.firstName': 'first_name';
  'personalDetails.lastName': 'last_name';
  'personalDetails.email': 'email_address';
  'personalDetails.phone': 'phone_number';
  'propertyLocation.country': 'country';
  'propertyLocation.state': 'state';
  'propertyLocation.city': 'city';
  'propertyLocation.streetAddress': 'street_address';
  'propertyLocation.addressLine2': 'address_line_2';
  'propertyLocation.zipCode': 'zip_code';
  'propertyLocation.latitude': 'latitude';
  'propertyLocation.longitude': 'longitude';
  'propertyDetails.propertyTypeSlug': 'property_type_slug';
  'propertyDetails.propertyCategory': 'property_category_slug';
  'propertyDetails.bedrooms': 'bedroom_size';
  'propertyDetails.propertyId': 'property_id';
  'propertyDetails.generatedTitle': 'generated_title';
  'tenancyInformation.moveInDate': 'move_in_date';
  'tenancyInformation.moveOutDate': 'move_out_date';
  'tenancyInformation.currentlyLivingThere': 'currently_living_there';
  'tenancyInformation.rentalHistory': 'rental_history';
  'financialInformation.rentPrice': 'rent_price';
  'financialInformation.rentPriceCurrency': 'rent_price_currency';
  'utilitiesElectricity.electricityType': 'electricity_type';
  'utilitiesElectricity.prepaidMeter': 'prepaid_meter';
  'utilitiesElectricity.electricityBand': 'electricity_band';
  'utilitiesElectricity.electricityTariffCurrency': 'electricity_tariff_currency';
  'utilitiesElectricity.tariffPerKwh': 'tarrif_per_kwh';
  'utilitiesElectricity.electricityDebtStatus': 'electricity_debt_status';
  'utilitiesElectricity.generatorType': 'generator_type';
  'utilitiesWater.waterStatus': 'water_status';
  'utilitiesWater.h20IsDrinkable': 'h20_is_drinkable';
  'propertyAmenities': 'property_amenities';
  'reviewContent.summaryRatings': 'summary_ratings';
  'reviewContent.pros': 'pros';
  'reviewContent.cons': 'cons';
  'reviewContent.detailedReview': 'detailed_review';
  'reviewContent.rentAgain': 'rent_again';
  'documents.propertyImages': 'tenant_property_images';
  'documents.identityDocuments': 'tenant_id_doc';     // ARRAY<JSONB>
  'documents.residencyDocuments': 'tenant_por_doc';   // ARRAY<JSONB>
  'commission.commissionIntent': 'commission_intent';
  'commission.commissionPayout': 'commission_payout';
  'submission.reviewStatus': 'review_status';
}

// ✅ NEW: Server action types for proper data handling
export interface ReviewSubmissionData {
  // All database fields properly typed
  tenant_id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  country: string;
  state: string;
  city: string;
  street_address?: string;
  address_line_2?: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  property_type_slug: string;
  property_category_slug?: string;
  bedroom_size?: string;
  property_id?: string;
  generated_title?: string;
  move_in_date: string;
  move_out_date?: string;
  currently_living_there?: boolean;
  rental_history?: any;
  rent_price_currency: 'NGN' | 'USD' | 'GBP' | 'EUR';
  rent_price: number;
  electricity_type: 'phcn' | 'generator' | 'inverter';
  prepaid_meter: 'yes' | 'no' | 'unknown';
  electricity_band: 'A' | 'B' | 'C' | 'D' | 'E' | 'unknown';
  electricity_tariff_currency?: string;
  tarrif_per_kwh: number;
  electricity_debt_status: 'paid' | 'owing' | 'unknown';
  generator_type?: 'diesel' | 'self_service' | 'none';
  water_status: 'treated' | 'fair' | 'bad' | 'unknown';
  h20_is_drinkable: 'yes' | 'no' | 'unknown';
  property_amenities: any;
  summary_ratings: any;
  pros: any;
  cons: any;
  detailed_review: string;
  rent_again: boolean;
  tenant_property_images?: any;
  tenant_id_doc?: Array<{
    name: string;
    type: string;
    size: number;
    filePath: string;
    uploadedAt: string;
    validationStatus: 'pending' | 'approved' | 'rejected';
  }> | null;
  tenant_por_doc?: Array<{
    name: string;
    type: string;
    size: number;
    filePath: string;
    uploadedAt: string;
    validationStatus: 'pending' | 'approved' | 'rejected';
  }> | null;
  commission_intent?: boolean;
  commission_payout?: any;
  review_status?: 'pending' | 'published' | 'rejected' | 'in review';
}

// ✅ NEW: Utility functions for data transformation
export function transformFormDataToDatabase(
  formData: LeaveReviewFormData,
  tenantId: string
): ReviewSubmissionData {
  return {
    tenant_id: tenantId,
    first_name: formData.personalDetails.firstName,
    last_name: formData.personalDetails.lastName,
    email_address: formData.personalDetails.email,
    phone_number: formData.personalDetails.phone,
    country: formData.propertyLocation.country,
    state: formData.propertyLocation.state,
    city: formData.propertyLocation.city,
    street_address: formData.propertyLocation.streetAddress || null,
    address_line_2: formData.propertyLocation.addressLine2 || null,
    zip_code: formData.propertyLocation.zipCode || null,
    latitude: formData.propertyLocation.latitude || null,
    longitude: formData.propertyLocation.longitude || null,
    property_type_slug: formData.propertyDetails.propertyTypeSlug,
    property_category_slug: formData.propertyDetails.propertyCategory || null,
    bedroom_size: formData.propertyDetails.bedrooms !== undefined ? String(formData.propertyDetails.bedrooms) : null,
    property_id: formData.propertyDetails.propertyId || null,
    generated_title: formData.propertyDetails.generatedTitle || null,
    move_in_date: formData.tenancyInformation.moveInDate,
    move_out_date: formData.tenancyInformation.moveOutDate || null,
    currently_living_there: formData.tenancyInformation.currentlyLivingThere,
    rental_history: formData.tenancyInformation.rentalHistory ? {
      history: formData.tenancyInformation.rentalHistory
    } : null,
    rent_price_currency: formData.financialInformation.rentPriceCurrency,
    rent_price: formData.financialInformation.rentPrice,
    electricity_type: formData.utilitiesElectricity.electricityType,
    prepaid_meter: formData.utilitiesElectricity.prepaidMeter,
    electricity_band: formData.utilitiesElectricity.electricityBand,
    electricity_tariff_currency: formData.utilitiesElectricity.electricityTariffCurrency || null,
    tarrif_per_kwh: formData.utilitiesElectricity.tariffPerKwh,
    electricity_debt_status: formData.utilitiesElectricity.electricityDebtStatus,
    generator_type: formData.utilitiesElectricity.generatorType || null,
    water_status: formData.utilitiesWater.waterStatus,
    h20_is_drinkable: formData.utilitiesWater.h20IsDrinkable,
    property_amenities: {
      selected: formData.propertyAmenities.selectedAmenities,
      by_category: formData.propertyAmenities.amenitiesByCategory
    },
    summary_ratings: formData.reviewContent.summaryRatings,
    pros: formData.reviewContent.pros,
    cons: formData.reviewContent.cons,
    detailed_review: formData.reviewContent.detailedReview,
    rent_again: formData.reviewContent.rentAgain,
    tenant_property_images: formData.documents.propertyImages.metadata.length > 0 ? {
      files: formData.documents.propertyImages.metadata
    } : null,
    tenant_id_doc: formData.documents.identityDocuments.metadata.length > 0 ? 
      formData.documents.identityDocuments.metadata.map(doc => ({
        name: doc.name,
        type: doc.type,
        size: doc.size,
        filePath: doc.filePath || `identity/${Date.now()}/${doc.name}`,
        uploadedAt: new Date().toISOString(),
        validationStatus: 'pending' as const
      })) : null,
    tenant_por_doc: formData.documents.residencyDocuments.metadata.length > 0 ? 
      formData.documents.residencyDocuments.metadata.map(doc => ({
        name: doc.name,
        type: doc.type,
        size: doc.size,
        filePath: doc.filePath || `residency/${Date.now()}/${doc.name}`,
        uploadedAt: new Date().toISOString(),
        validationStatus: 'pending' as const
      })) : null,
    commission_intent: formData.commission.commissionIntent,
    commission_payout: formData.commission.commissionPayout || null,
    review_status: formData.submission.reviewStatus,
  };
}

// ✅ NEW: Generate unique reference ID
export function generateReferenceId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 7);
  return `RW${timestamp}${random}`.toUpperCase().substring(0, 16);
}

// ✅ NEW: Validation helpers
export function validateFormStep(step: string, data: LeaveReviewFormData): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  switch (step) {
    case 'verification':
      if (!data.personalDetails.firstName.trim()) errors.push('First name is required');
      if (!data.personalDetails.lastName.trim()) errors.push('Last name is required');
      if (!data.personalDetails.email.trim()) errors.push('Email is required');
      if (!data.personalDetails.phone.trim()) errors.push('Phone number is required');
      if (!data.verification.isVerified) errors.push('Phone verification is required');
      break;

    case 'property-details':
      if (!data.propertyLocation.country.trim()) errors.push('Country is required');
      if (!data.propertyLocation.state.trim()) errors.push('State is required');
      if (!data.propertyLocation.city.trim()) errors.push('City is required');
      if (!data.propertyDetails.propertyTypeSlug.trim()) errors.push('Property type is required');
      if (!data.tenancyInformation.moveInDate.trim()) errors.push('Move-in date is required');
      
      // Move-out date validation with "currently living here" logic
      if (!data.tenancyInformation.currentlyLivingThere && !data.tenancyInformation.moveOutDate?.trim()) {
        errors.push('Move-out date is required unless you are currently living at the property');
      }
      
      if (data.financialInformation.rentPrice <= 0) errors.push('Rent price must be greater than 0');
      break;

    case 'experience': {
      const ratings = data.reviewContent.summaryRatings;
      if (!ratings.overall || ratings.overall < 1 || ratings.overall > 5) errors.push('Overall rating is required (1-5)');
      if (!ratings.condition || ratings.condition < 1 || ratings.condition > 5) errors.push('Condition rating is required (1-5)');
      if (!ratings.value || ratings.value < 1 || ratings.value > 5) errors.push('Value rating is required (1-5)');
      if (!ratings.management || ratings.management < 1 || ratings.management > 5) errors.push('Management rating is required (1-5)');
      if (!ratings.safety || ratings.safety < 1 || ratings.safety > 5) errors.push('Safety rating is required (1-5)');
      if (!ratings.price || ratings.price < 1 || ratings.price > 5) errors.push('Price rating is required (1-5)');
      
      if (data.reviewContent.detailedReview.trim().length < 120) {
        errors.push('Review must be at least 120 characters long');
      }
      if (data.reviewContent.pros.length === 0) errors.push('At least one pro is required');
      if (data.reviewContent.cons.length === 0) errors.push('At least one con is required');
      break;
    }

    case 'documents': {
      // ✅ 2025 COMPLIANT: Use metadata for validation (robust against File object serialization issues)
      const propertyImagesCount = data.documents.propertyImages.metadata?.length || 0;
      const identityDocsCount = data.documents.identityDocuments.metadata?.length || 0;
      const residencyDocsCount = data.documents.residencyDocuments.metadata?.length || 0;
      
      if (propertyImagesCount < 3) {
        errors.push('At least 3 property images are required');
      }
      if (propertyImagesCount > 6) {
        errors.push('Maximum 6 property images allowed');
      }
      if (identityDocsCount < 1) {
        errors.push('At least 1 identity document is required');
      }
      if (identityDocsCount > 3) {
        errors.push('Maximum 3 identity documents allowed');
      }
      if (residencyDocsCount < 1) {
        errors.push('At least 1 proof of residency document is required');
      }
      if (residencyDocsCount > 3) {
        errors.push('Maximum 3 proof of residency documents allowed');
      }
      break;
    }

    case 'submit':
      if (!data.submission.termsAccepted.terms) errors.push('Terms and conditions must be accepted');
      break;
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

// ✅ NEW: Constants for database enums (useful for dropdowns)
export const DATABASE_ENUMS = {
  RENT_PRICE_CURRENCY: ['NGN', 'USD', 'GBP', 'EUR'] as const,
  ELECTRICITY_TYPE: ['phcn', 'generator', 'inverter'] as const,
  PREPAID_METER: ['yes', 'no', 'unknown'] as const,
  ELECTRICITY_BAND: ['A', 'B', 'C', 'D', 'E', 'unknown'] as const,
  ELECTRICITY_DEBT_STATUS: ['paid', 'owing', 'unknown'] as const,
  GENERATOR_TYPE: ['diesel', 'self_service', 'none'] as const,
  WATER_STATUS: ['treated', 'fair', 'bad', 'unknown'] as const,
  H20_IS_DRINKABLE: ['yes', 'no', 'unknown'] as const,
  REVIEW_STATUS: ['pending', 'published', 'rejected', 'in review'] as const,
} as const; 